import { Build, ContentCopy, Delete, Edit, PauseCircleOutline } from '@mui/icons-material';
import {
    Box,
    Chip,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import React from 'react';
import { formatCurrencyInCents, presentUuid } from '../../helpers/formatters';
import { formatFilamentGrams, formatProductionDurationMinutes } from '../../helpers/jobs';
import { getPrinterBrandBadgeProps } from '../../helpers/printerBrand';
import {
    formatMaintenanceDurationLiteral,
    MAINTENANCE_DOWNTIME_SUFFIX,
} from '../../helpers/printersOverview';
import { liveUpdateHighlightSx } from '../../helpers/liveUpdateHighlight';
import {
    replacementPartsCountLabel,
} from '../../helpers/printerMaintenances';
import PrinterRuntimeStatus from './PrinterRuntimeStatus';
import PrinterSnapshotThumb from './PrinterSnapshotThumb';
import PrinterFilamentSlots from './PrinterFilamentSlots';

export default function PrintersOverviewTable({
    printers,
    highlightedPrinterIds,
    onEdit,
    onMaintenances,
    onDelete,
    onCopyUuid,
    onProductivity,
}) {
    return (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Impressora</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Consumo</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Valor pago</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Produtividade</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Manutenções</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">Ações</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(printers || []).map((printer) => {
                        const brandBadge = getPrinterBrandBadgeProps(printer.brand);
                        const successRate = Number(printer.success_rate) || 0;
                        const downtimeMinutes = Number(printer.maintenance_downtime_minutes) || 0;
                        const partsCount = Number(printer.parts_count) || 0;
                        const partsTotalCents = Number(printer.parts_total_cents) || 0;
                        const printerId = printer.printer_uuid || printer.id;
                        const brandModel = [printer.brand, printer.model]
                            .map((part) => String(part || '').trim())
                            .filter(Boolean)
                            .join(' - ');

                        return (
                            <TableRow
                                key={printerId}
                                hover
                                sx={liveUpdateHighlightSx(Boolean(highlightedPrinterIds?.[printerId]))}
                            >
                                <TableCell sx={{ verticalAlign: 'top', minWidth: 220 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                        <PrinterSnapshotThumb
                                            printer={printer}
                                            size="row"
                                            alt={`Câmera de ${printer.name || 'impressora'}`}
                                        />
                                        <Box sx={{ minWidth: 0 }}>
                                            {brandBadge ? (
                                                <Chip label={printer.name} size="small" {...brandBadge} />
                                            ) : (
                                                <Typography variant="body2" fontWeight={700}>
                                                    {printer.name}
                                                </Typography>
                                            )}
                                            {brandModel ? (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {brandModel}
                                                </Typography>
                                            ) : null}
                                            <Box sx={{ mt: 0.75 }}>
                                                <PrinterFilamentSlots
                                                    printerUuid={printerId}
                                                    printerName={printer.name}
                                                    filaments={printer.filaments}
                                                    restriction={printer.productivity_restriction}
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mt: 0.25 }}>
                                                <Tooltip title={printerId}>
                                                    <Typography
                                                        variant="caption"
                                                        component="span"
                                                        sx={{ fontFamily: 'monospace' }}
                                                    >
                                                        {presentUuid(printerId)}
                                                    </Typography>
                                                </Tooltip>
                                                <Tooltip title="Copiar UUID completo para o Farm Link">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => onCopyUuid?.(printerId)}
                                                        aria-label="Copiar UUID da impressora"
                                                    >
                                                        <ContentCopy sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                            <Box sx={{ mt: 0.75 }}>
                                                <PrinterRuntimeStatus
                                                    isPrinting={printer.is_printing}
                                                    isIdle={printer.is_idle}
                                                    idleLabel={printer.idle_label}
                                                    lastActivityAt={printer.last_activity_at}
                                                    restriction={printer.productivity_restriction}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ verticalAlign: 'top' }}>
                                    <Typography variant="body2">
                                        Médio: {printer.avg_consumption_watts ?? '—'} W
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Máximo: {printer.consumption ?? '—'} W
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ verticalAlign: 'top' }}>
                                    <Typography variant="body2" fontWeight={600}>
                                        {formatCurrencyInCents(printer.price || 0)}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ verticalAlign: 'top' }}>
                                    <Typography variant="body2" fontWeight={700}>
                                        {successRate.toFixed(1).replace('.', ',')}%
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {formatProductionDurationMinutes(printer.total_duration_minutes)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {formatFilamentGrams(printer.total_filament_grams)}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ verticalAlign: 'top' }}>
                                    <Typography variant="body2">
                                        {Number(printer.maintenance_count) || 0}
                                        {downtimeMinutes > 0 && (
                                            <Typography component="span" variant="caption" color="text.secondary">
                                                {' '}({formatMaintenanceDurationLiteral(downtimeMinutes)}{MAINTENANCE_DOWNTIME_SUFFIX})
                                            </Typography>
                                        )}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        Peças: {replacementPartsCountLabel(partsCount)}
                                        {' '}({formatCurrencyInCents(partsTotalCents)})
                                    </Typography>
                                </TableCell>
                                <TableCell align="right" sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                    <IconButton color="primary" onClick={() => onEdit?.(printerId)} aria-label="editar">
                                        <Edit />
                                    </IconButton>
                                    <Tooltip title={printer.productivity_restriction ? 'Restrição de produção' : 'Marcar como não produtiva'}>
                                        <IconButton
                                            color={printer.productivity_restriction ? 'warning' : 'primary'}
                                            onClick={() => onProductivity?.(printer)}
                                            aria-label="restrição de produção"
                                        >
                                            <PauseCircleOutline />
                                        </IconButton>
                                    </Tooltip>
                                    <IconButton color="primary" onClick={() => onMaintenances?.(printerId)} aria-label="manutenções">
                                        <Build />
                                    </IconButton>
                                    <IconButton color="error" onClick={() => onDelete?.(printerId)} aria-label="remover">
                                        <Delete />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
